import { Radio } from '@dealstack/frontend/shared/components/fundament/Radio/Radio';
import { RadioGroup } from '@dealstack/frontend/shared/components/forms/RadioGroup/RadioGroup';
import { RepresentativePersonalInformationInput } from '@dealstack/frontend/investor/pages/Investment/common/personalDetails/RepresentativePersonalInformationInput';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex, Title } from '@mantine/core';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { JOIN_ALLOCATION_PIV_INFO_FORM } from './useJoinAndAllocationPIVInformationController';
import {
	ControllingPartyEntry,
	PIVInfoForm,
	SIGNATORY_CHOICE,
} from './useJoinAndAllocationPIVInformationController.types';

interface Props {
	disabled?: boolean;
	investorPersonId?: string;
}

const partyDisplayName = (party: ControllingPartyEntry): string =>
	[party.firstName, party.lastName].filter(Boolean).join(' ').trim() || party.email;

const partyPersonKey = (party: ControllingPartyEntry): string =>
	(party.email || partyDisplayName(party)).trim().toLowerCase();

export const AuthorisedSignatorySection = ({ disabled, investorPersonId }: Props) => {
	const tInvestor = useTranslationInvestor();
	const { control } = useFormContext<PIVInfoForm>();

	const isPivInvestment = useWatch({ control, name: JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV });
	const controllingParties =
		useWatch({ control, name: JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES }) ?? [];
	const signatoryChoice = useWatch({
		control,
		name: JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE,
	});
	const savedSignatory = useWatch({
		control,
		name: JOIN_ALLOCATION_PIV_INFO_FORM.SAVED_SIGNATORY,
	});

	const seenPersonKeys = new Set<string>();
	const namedParties = isPivInvestment
		? controllingParties.filter((party) => {
				if (partyDisplayName(party).length === 0) {
					return false;
				}
				// The investor is already covered by "Myself" — don't list them
				// again even if they're also a controlling party.
				if (investorPersonId && party.personId === investorPersonId) {
					return false;
				}
				const key = partyPersonKey(party);
				if (seenPersonKeys.has(key)) {
					return false;
				}
				seenPersonKeys.add(key);
				return true;
			})
		: [];

	return (
		<Flex direction="column" gap="1rem">
			<Title order={5}>{tInvestor('investment.piv.signatory.question')}</Title>

			<StyledRadioGroup
				name={JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE}
				disabled={disabled}
			>
				<Radio
					value={SIGNATORY_CHOICE.SELF}
					label={tInvestor('investment.piv.signatory.myself')}
				/>
				{namedParties.map((party) => (
					<Radio
						key={party.localId}
						value={party.localId}
						label={partyDisplayName(party)}
					/>
				))}
				{isPivInvestment && savedSignatory && (
					<Radio
						value={SIGNATORY_CHOICE.EXISTING_REPRESENTATIVE}
						label={
							[savedSignatory.firstName, savedSignatory.lastName]
								.filter(Boolean)
								.join(' ')
								.trim() || savedSignatory.email
						}
					/>
				)}
				<Radio
					value={SIGNATORY_CHOICE.OTHER}
					label={tInvestor('investment.piv.signatory.someone_else')}
				/>
			</StyledRadioGroup>

			{signatoryChoice === SIGNATORY_CHOICE.OTHER && (
				<RepresentativePersonalInformationInput
					firstNameFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME}
					lastNameFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME}
					emailFieldName={JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL}
					disabled={disabled}
				/>
			)}
		</Flex>
	);
};

const StyledRadioGroup = styled(RadioGroup)`
	align-items: flex-start;

	*:hover {
		background: none;
	}
`;
